/**
 * Story 134 — the ONE map from the status name Sun sends to the label the screen paints (D1).
 *
 * Sun sends the name upper-cased (DRAFT | PROPOSAL | APPROVED), both per version (`indexValue`) and
 * aggregated over every version of a code (`overallIndexValue`). Both go through here, so the chip of
 * a version and the Status column of the listing can NEVER be labelled differently.
 *
 * Story 204 (RN1.5): the i18n key of a status is the status code itself (`DRAFT`/`PROPOSAL`/`APPROVED`, enum
 * `FeatureGroupRevisionState`), translated through systemValueLabel; the old keys `Draft`/`Proposal`/`Approved` no
 * longer label it.
 *
 * ⚠️ The listing's Status column reads `overallIndexValue`, never `currentIndexValue`: the two mean
 * different things and the second one is what the workflow editor's link dialog depends on.
 */
import { systemValueLabel } from '../../generic/systemValues/systemValueLabel'

export const BOM_STATUS_DRAFT = 'DRAFT'
export const BOM_STATUS_PROPOSAL = 'PROPOSAL'
export const BOM_STATUS_APPROVED = 'APPROVED'

/** Every value the catalogue can hold, in the precedence order Sun applies (RN-134-03). */
export const BOM_STATUSES = [BOM_STATUS_DRAFT, BOM_STATUS_PROPOSAL, BOM_STATUS_APPROVED]

/**
 * MUI chip colour per status, as the mockup 'MIC Biblioteca de BOMs' paints them (PO, 2026-09-15):
 * Borrador in amber (warning), the approved one in green (success). Proposal is not in the mockup;
 * info keeps it apart from both. Grey for the draft was invisible next to the other two.
 */
const CHIP_COLORS = {
  [BOM_STATUS_DRAFT]: 'warning',
  [BOM_STATUS_PROPOSAL]: 'info',
  [BOM_STATUS_APPROVED]: 'success'
}

const normalize = (status) =>
  typeof status === 'string' ? status.trim().toUpperCase() : ''

/**
 * The i18n KEY for a status — the normalized code itself (RN1.5) — or `''` when it is absent or unknown. An absent
 * status is a real answer: a BOM whose versions carry no index has no aggregate status, and the cell is simply empty.
 */
export const bomStatusLabelKey = (status) => (isKnownBomStatus(status) ? normalize(status) : '')

/** Translated label, or `''` when the status is absent or unknown. Never prints the raw code. */
export const bomStatusLabel = (status, t_) => {
  const key = bomStatusLabelKey(status)
  return key ? systemValueLabel(t_, 'FeatureGroupRevisionState', key) : ''
}

export const bomStatusColor = (status) => CHIP_COLORS[normalize(status)] || 'default'

/** A status the catalogue does not know is treated as absent, never painted raw. */
export const isKnownBomStatus = (status) => BOM_STATUSES.includes(normalize(status))
